import {
	GovernedChannelType,
	IAppGovernanceProvider,
	IGovernanceParameters,
	IGovernanceServiceClient,
	IGovernedChannelData,
} from "@ms-ofb/floodgate-shared-typings";
import { Governor } from "../Governor";
import { IGovernedChannelStateProvider } from "../GovernedChannelStateProvider";
import { CampaignBusinessLevel, GovernanceDecision, IGovernanceDecision, IOrchestrationEngine } from "./Api";
import { IAppGovernanceCampaignForm } from "@ms-ofb/floodgate-shared-typings/dist/src/Core/Api/IAppGovernanceProvider";

export class OrchestrationEngine implements IOrchestrationEngine {
	private governanceServiceClient: IGovernanceServiceClient;
	private channelGovernanceProvider: Governor;
	private appGovernanceProvider: IAppGovernanceProvider;

	public constructor(governanceServiceClient: IGovernanceServiceClient, 
		governedChannelStateProvider: IGovernedChannelStateProvider,
		appGovernanceProvider: IAppGovernanceProvider) {

		if (!governanceServiceClient) {
			throw new Error("Invalid governanceServiceClient");
		}
	
		if (!governedChannelStateProvider) {
			throw new Error("Invalid governanceServiceClient");
		}

		this.governanceServiceClient = governanceServiceClient;
		this.channelGovernanceProvider = new Governor(governedChannelStateProvider);
		this.appGovernanceProvider = appGovernanceProvider;
	}

	public async getDecision(businessLevel: CampaignBusinessLevel, governanceParams: IGovernanceParameters): Promise<IGovernanceDecision> {
		if (!governanceParams) {
			throw new Error("Invalid governanceParams");
		}

		const defaultDecision: IGovernanceDecision = {
			governanceDecision: GovernanceDecision.Approved,
			serviceResult: {
				permission: true
			}
		};

		// Always allow business critical campaigns
		if (businessLevel === CampaignBusinessLevel.Critical) {
			return defaultDecision;
		}

		// Allow calls only on the channels that are governed
		if (!this.governanceServiceClient.isEnabled(governanceParams.channelType)) {
			return defaultDecision;
		}

		// Update governance cooldown if the value is not set
		if (governanceParams.userCooldown < 0) {
			governanceParams.userCooldown = Governor.GetChannelCoolDown(governanceParams.channelType);
		}

		// check with governance service
		const serviceResult = await this.governanceServiceClient.checkGovernancePermitAsync(governanceParams);
		let governanceDecision: GovernanceDecision = GovernanceDecision.Error;
		if (serviceResult && !serviceResult.error) {
			governanceDecision = serviceResult.permission ? GovernanceDecision.Approved : GovernanceDecision.Rejected;
		}

		return {
			governanceDecision,
			serviceResult
		};
	}

	public isSurveyTrackable(campaignForm: IAppGovernanceCampaignForm): boolean {
		return !this.appGovernanceProvider || this.appGovernanceProvider.getDecision(campaignForm);
	}

	public getAvailableChannels(): GovernedChannelType[] {
		const channels: IGovernedChannelData[] = this.channelGovernanceProvider.getAvailableChannelData();
		const channelTypes: GovernedChannelType[] = [];

		channels.forEach((channel) => {
			if (channel) {
				channelTypes.push(channel.getType());
			}
		});

		return channelTypes;
	}

	public mergeAndSave(): void {
		this.channelGovernanceProvider.saveChannelStates();
	}

	public isChannelOpen(governedChannelType: GovernedChannelType, refreshData = true): boolean {
		// Refresh channels
		if (refreshData) {
			this.channelGovernanceProvider.refreshChannelData();
		}

		return this.channelGovernanceProvider.isChannelOpen(governedChannelType);
	}

	public isChannelGovernanceEnabled(governedChannelType: GovernedChannelType): boolean {
		return this.governanceServiceClient.isEnabled(governedChannelType);
	}

	public startChannelCooldown(channelType: GovernedChannelType, dateTime: Date): void {
		this.channelGovernanceProvider.startChannelCooldown(channelType, dateTime);
	}

	public shouldForcePermission(): boolean {
		return this.governanceServiceClient.shouldForceDisplay();
	}

	public getStateForTelemetry(): any {
		return this.governanceServiceClient.getClientState();
	}
}
